/**
 * useEntityRecordRegistry
 *
 * Cache-first orchestration for EntityRecord selection (large datasets).
 *
 * Subscribes to the registry singleton for the given entity/group on mount,
 * reads cached records via `useSnapshot` for granular reactivity, and delegates
 * search to `useEntityRecordSearch` (Algolia is only queried after the
 * min-char threshold).
 *
 * UI components consume this hook directly. The underlying singleton
 * (`entityRecordsRegistryStore`) is a private implementation detail.
 *
 * `isRecordMissing` reads this bucket's `misses` off the snapshot, so a render
 * that asks re-renders when a `getRecord` read settles as absent. A failed read
 * is not missing: the record may well exist.
 *
 * The registry maps are keyed across every entity/group in play, so only the
 * single resolved bucket for this key may reach a dependency array — putting
 * `snap.records` there recomputes whenever an unrelated entity refreshes.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';

import type { EntityRecord } from '#types/System.types';

import { useEntityRecordSearch } from '@/use/useEntityRecordSearch';
import {
    getRecord,
    registerCommonRecordUsage,
    subscribe,
} from './actions';
import { cacheKey, entityRecordsRegistryStore } from './store';

const EMPTY_RECORDS: EntityRecord[] = [];

interface UseEntityRecordRegistryParams {
    entityId: string;
    entityGroupId?: string;
}

interface UseEntityRecordRegistryReturn {
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    entityRecords: EntityRecord[];
    loading: boolean;
    error: Error | null;
    getRecord: (entityId: string, recordId: string, entityGroupId?: string) => EntityRecord | undefined;
    isRecordMissing: (recordId: string) => boolean;
    registerCommonRecordUsage: (entityId: string, recordId: string) => void;
}

export const useEntityRecordRegistry = ({
    entityId,
    entityGroupId,
}: UseEntityRecordRegistryParams): UseEntityRecordRegistryReturn => {
    const [searchQuery, setSearchQueryState] = useState<string>('');

    useEffect(() => {
        if (!entityId) return;
        return subscribe(entityId, entityGroupId);
    }, [entityId, entityGroupId]);

    const snap = useSnapshot(entityRecordsRegistryStore);
    const key = cacheKey(entityId, entityGroupId);

    const cacheRecords = (entityId
        ? (snap.records.get(key) as EntityRecord[] | undefined)
        : undefined) ?? EMPTY_RECORDS;

    const {
        searchResults,
        isSearching,
        searchError,
        search,
        clearSearch,
    } = useEntityRecordSearch({
        entityId,
        entityGroupId,
        cacheRecords,
    });

    const setSearchQuery = useCallback((query: string) => {
        setSearchQueryState(query);

        if (query.trim()) {
            search(query);
            return;
        }

        clearSearch();
    }, [clearSearch, search]);

    const entityRecords = useMemo(() => {
        if (!searchQuery.trim()) return cacheRecords;
        return searchResults;
    }, [cacheRecords, searchQuery, searchResults]);

    const registryLoading = entityId ? (snap.loading.get(key) ?? false) : false;
    const registryError = entityId ? (snap.errors.get(key) ?? null) : null;

    const combinedError = registryError || (searchError ? new Error(searchError) : null);

    return {
        searchQuery,
        setSearchQuery,
        entityRecords,
        loading: registryLoading || isSearching,
        error: combinedError,
        getRecord,
        isRecordMissing: (recordId: string) => snap.misses.get(key)?.[recordId]?.outcome === 'absent',
        registerCommonRecordUsage,
    };
};
